<template>
  <div
    class="modal fade"
    id="profileModal"
    tabindex="-1"
    aria-labelledby="profileModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content profile-modal">
        <div class="modal-header">
          <h5 class="modal-title" id="profileModalLabel">{{ $t("ui.profile") }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" :aria-label="$t('ui.cancel')"></button>
        </div>
        <div class="modal-body">
          <section class="profile-section">
            <h6>{{ $t("ui.profileUserId") }}</h6>
            <div class="profile-userid">
              <code>{{ userId || "—" }}</code>
              <button
                v-if="userId"
                type="button"
                class="btn btn-sm btn-outline-secondary"
                :disabled="copyingId"
                @click="copyUserId"
              >
                {{ idCopied ? $t("ui.profileUserIdCopied") : $t("ui.profileCopyUserId") }}
              </button>
            </div>
          </section>

          <section class="profile-section">
            <h6>{{ $t("ui.profileEmail") }}</h6>
            <label class="profile-field">
              <span>{{ $t("ui.profileEmail") }}</span>
              <input v-model="email" type="email" autocomplete="username" class="form-control" />
            </label>
            <p v-if="emailError" class="profile-error">{{ emailError }}</p>
            <p v-if="emailSuccess" class="profile-success">{{ emailSuccess }}</p>
            <button
              type="button"
              class="btn profile-action"
              :class="emailReady ? 'btn-primary' : 'btn-outline-secondary'"
              :disabled="emailSaving || !emailReady"
              @click="saveEmail"
            >
              {{ emailSaving ? $t("ui.profileSaving") : $t("ui.profileSaveEmail") }}
            </button>
          </section>

          <section class="profile-section">
            <h6>{{ $t("ui.profilePassword") }}</h6>
            <label class="profile-field">
              <span>{{ $t("ui.profileCurrentPassword") }}</span>
              <input
                v-model="currentPassword"
                type="password"
                autocomplete="current-password"
                class="form-control"
              />
            </label>
            <label class="profile-field">
              <span>{{ $t("ui.profileNewPassword") }}</span>
              <input
                v-model="newPassword"
                type="password"
                autocomplete="new-password"
                class="form-control"
              />
            </label>
            <p v-if="passwordError" class="profile-error">{{ passwordError }}</p>
            <p v-if="passwordSuccess" class="profile-success">{{ passwordSuccess }}</p>
            <button
              type="button"
              class="btn profile-action"
              :class="passwordReady ? 'btn-primary' : 'btn-outline-secondary'"
              :disabled="passwordSaving || !passwordReady"
              @click="savePassword"
            >
              {{ passwordSaving ? $t("ui.profileSaving") : $t("ui.profileSavePassword") }}
            </button>
          </section>

          <section class="profile-section profile-logout">
            <button
              type="button"
              class="btn btn-outline-danger w-100"
              :disabled="loggingOut"
              @click="doLogout"
            >
              {{ loggingOut ? $t("ui.profileLoggingOut") : $t("ui.profileLogout") }}
            </button>
            <p v-if="logoutError" class="profile-error">{{ logoutError }}</p>
          </section>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
const { updateEmail, updatePassword, logout } = require("../sync/engine");

export default {
  name: "ProfileModal",
  props: {
    user: {
      type: Object,
      default: null,
    },
  },
  emits: ["logged-out", "email-updated"],
  data() {
    return {
      email: "",
      emailSaving: false,
      emailError: "",
      emailSuccess: "",
      currentPassword: "",
      newPassword: "",
      passwordSaving: false,
      passwordError: "",
      passwordSuccess: "",
      loggingOut: false,
      logoutError: "",
      copyingId: false,
      idCopied: false,
      copyTimer: null,
    };
  },
  computed: {
    userId() {
      return (this.user && this.user.id) || "";
    },
    emailReady() {
      const current = (this.user && this.user.email) || "";
      return this.email.trim().toLowerCase() !== current.trim().toLowerCase();
    },
    passwordReady() {
      return this.currentPassword.length > 0 && this.newPassword.length >= 8;
    },
  },
  mounted() {
    const el = document.getElementById("profileModal");
    if (el) {
      el.addEventListener("show.bs.modal", this.resetForm);
    }
  },
  beforeUnmount() {
    const el = document.getElementById("profileModal");
    if (el) {
      el.removeEventListener("show.bs.modal", this.resetForm);
    }
    if (this.copyTimer) {
      clearTimeout(this.copyTimer);
      this.copyTimer = null;
    }
  },
  methods: {
    resetForm() {
      this.email = (this.user && this.user.email) || "";
      this.emailError = "";
      this.emailSuccess = "";
      this.currentPassword = "";
      this.newPassword = "";
      this.passwordError = "";
      this.passwordSuccess = "";
      this.logoutError = "";
      this.idCopied = false;
    },
    async copyUserId() {
      if (!this.userId || !navigator.clipboard) return;
      this.copyingId = true;
      try {
        await navigator.clipboard.writeText(this.userId);
        this.idCopied = true;
        if (this.copyTimer) clearTimeout(this.copyTimer);
        this.copyTimer = setTimeout(() => {
          this.idCopied = false;
          this.copyTimer = null;
        }, 1500);
      } catch {
        // ignore clipboard failures
      } finally {
        this.copyingId = false;
      }
    },
    hideModal() {
      const el = document.getElementById("profileModal");
      if (!el) return;
      const instance = Modal.getInstance(el) || new Modal(el);
      instance.hide();
    },
    async saveEmail() {
      this.emailError = "";
      this.emailSuccess = "";
      this.emailSaving = true;
      try {
        const updated = await updateEmail(this.email.trim());
        this.$emit("email-updated", updated);
        this.emailSuccess = this.$t("ui.profileEmailUpdated");
      } catch (error) {
        this.emailError = error.message || this.$t("ui.profileSaveFailed");
      } finally {
        this.emailSaving = false;
      }
    },
    async savePassword() {
      this.passwordError = "";
      this.passwordSuccess = "";
      this.passwordSaving = true;
      try {
        await updatePassword(this.currentPassword, this.newPassword);
        this.currentPassword = "";
        this.newPassword = "";
        this.passwordSuccess = this.$t("ui.profilePasswordUpdated");
      } catch (error) {
        this.passwordError = error.message || this.$t("ui.profileSaveFailed");
      } finally {
        this.passwordSaving = false;
      }
    },
    async doLogout() {
      this.logoutError = "";
      this.loggingOut = true;
      try {
        await logout();
        this.hideModal();
        this.$emit("logged-out");
      } catch (error) {
        this.logoutError = error.message || this.$t("ui.profileSaveFailed");
      } finally {
        this.loggingOut = false;
      }
    },
  },
};
</script>

<style scoped>
.profile-modal .modal-body {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.profile-section {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.profile-section h6 {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-transform: uppercase;
  color: #656d76;
}

.profile-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  font-size: 14px;
  color: #1f2328;
}

.profile-userid {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.profile-userid code {
  flex: 1;
  min-width: 0;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 12px;
  word-break: break-all;
  color: #1f2328;
  background: rgba(120, 120, 128, 0.12);
}

.profile-action {
  align-self: flex-start;
  min-width: 7.5rem;
}

.profile-action.btn-outline-secondary:disabled {
  opacity: 0.45;
  color: #8b949e;
  border-color: rgba(140, 149, 159, 0.45);
  background: transparent;
}

.profile-action.btn-primary:not(:disabled) {
  box-shadow: 0 1px 2px rgba(13, 110, 253, 0.25);
}

.profile-logout {
  padding-top: 4px;
  border-top: 1px solid rgba(60, 60, 67, 0.18);
}

.profile-error {
  margin: 0;
  font-size: 13px;
  color: #b42318;
}

.profile-success {
  margin: 0;
  font-size: 13px;
  color: #027a48;
}
</style>
